'use client';

import { useEffect, useRef, useState } from 'react';
import {
  acceptedVideoMimeTypes,
  formatVideoDurationMs,
  useLoadedVideoSource,
} from '@/components/tools/video-tool-shared';
import { extractVideoFramesToZip } from '@/lib/browser-video-tools';
import { clamp, formatFileSize, getBaseName } from '@/lib/browser-image-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';

// Defaults to PNG so the existing video-to-image-sequence page is unchanged; the
// mp4-to-jpg page passes format="jpg". The label is derived, not duplicated.
export default function BrowserVideoToImageSequence({
  format = 'png',
  heading,
  toolSlug,
}: {
  format?: 'png' | 'jpg';
  heading?: string;
  toolSlug?: string;
}) {
  const formatLabel = format.toUpperCase();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource } = useLoadedVideoSource();
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(0);
  const [fps, setFps] = useState(2);
  const [frameCount, setFrameCount] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (outputUrl) {
        URL.revokeObjectURL(outputUrl);
      }
    };
  }, [outputUrl]);

  useEffect(() => {
    if (!source) {
      return;
    }

    setStartMs(0);
    setEndMs(Math.min(source.metadata.durationMs, 5000));
  }, [source]);

  function resetOutputState() {
    if (outputUrl) {
      URL.revokeObjectURL(outputUrl);
    }

    setOutputUrl(null);
    setDownloadName(null);
    setResultSize(null);
    setFrameCount(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The video could not be loaded.');
    }
  }

  async function runExport() {
    if (!source) {
      setErrorMessage('Choose a video first.');
      return;
    }

    if (endMs <= startMs) {
      setErrorMessage('Set an end time that is later than the start time.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        toolSlug ?? 'video-to-image-sequence',
        'Credits are not available for another image-sequence export right now.',
      );
      const result = await extractVideoFramesToZip({
        file: source.file,
        startMs,
        endMs,
        fps,
        format,
      });
      const nextOutputUrl = URL.createObjectURL(result.zipBlob);

      resetOutputState();
      setOutputUrl(nextOutputUrl);
      setDownloadName(`${getBaseName(source.file.name)}-frames.zip`);
      setResultSize(result.zipBlob.size);
      setFrameCount(result.frameCount);
      setCreditMessage(formatToolCreditSuccess('Video frame export', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The frame ZIP could not be exported.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{heading ?? `Export a ${formatLabel} image sequence from video`}</h2>
          <p>
            Pick a short range, choose the frame rate, and package the extracted {formatLabel}{' '}
            frames as ZIP.
          </p>
        </div>
        <span className="tool-live-badge">ZIP</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one video or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Short ranges work best for local ZIP export'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept={acceptedVideoMimeTypes}
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Start (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setStartMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={startMs}
              />
            </label>
            <label className="tool-live-field">
              <span>End (ms)</span>
              <input
                min="0"
                onChange={(event) => {
                  setEndMs(Math.max(0, Number(event.target.value) || 0));
                  resetOutputState();
                }}
                type="number"
                value={endMs}
              />
            </label>
          </div>

          <label className="tool-live-field">
            <span>FPS</span>
            <input
              min="1"
              max="30"
              onChange={(event) => {
                setFps(clamp(Number(event.target.value) || 1, 1, 30));
                resetOutputState();
              }}
              type="number"
              value={fps}
            />
          </label>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runExport()}
              type="button"
            >
              {isProcessing ? 'Extracting…' : `Export ${formatLabel} sequence`}
            </button>
            {outputUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={outputUrl}>
                Download ZIP
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Sequence details</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Source duration</strong>
              <span>
                {source ? formatVideoDurationMs(source.metadata.durationMs) : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Range length</strong>
              <span>
                {source ? formatVideoDurationMs(Math.max(0, endMs - startMs)) : 'No video loaded'}
              </span>
            </div>
            <div>
              <strong>Frames exported</strong>
              <span>{frameCount ?? 'No export yet'}</span>
            </div>
            <div>
              <strong>ZIP size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'No export yet'}</span>
            </div>
          </div>

          <div className="tool-live-panel-note">
            <strong>Output</strong>
            <span>Frames are exported as numbered {formatLabel} files inside one ZIP.</span>
          </div>
        </aside>
      </div>
    </article>
  );
}
